import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import ProductNav from '../../components/feature/ProductNav';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

const faqs = [
  {
    q: 'What is mortgage protection insurance?',
    a: "Mortgage protection insurance is a life insurance policy that pays off your mortgage balance if you die before it's paid off. Your family keeps the home without the burden of mortgage payments. Unlike traditional PMI (which protects the lender), mortgage protection protects YOUR family.",
  },
  {
    q: 'How is mortgage protection different from regular life insurance?',
    a: 'Regular term life insurance pays a fixed death benefit directly to your beneficiaries to use however they choose. Mortgage protection is specifically designed to match your mortgage balance — it can also include living benefits like disability protection. Many Indiana homeowners actually prefer a larger term life policy that covers the mortgage AND provides income replacement.',
  },
  {
    q: 'Do I need a medical exam to qualify?',
    a: 'Many mortgage protection policies use simplified underwriting — no medical exam, just a few health questions. Even applicants with health conditions like diabetes or high blood pressure can often qualify. Fully underwritten policies are also available at lower premiums for those in good health.',
  },
  {
    q: 'How much does mortgage protection cost?',
    a: 'For a typical $250,000 mortgage, a healthy 35-year-old can expect to pay $25–$50/month for a 30-year term policy. Rates depend on your age, health, loan balance, and term length. We shop multiple carriers to find you the best rate.',
  },
  {
    q: 'Can I get mortgage protection with disability coverage?',
    a: 'Yes! Many mortgage protection policies include a waiver of premium rider or disability income rider, so your mortgage payments are covered if you become disabled — not just if you die. This is one of the most valuable add-ons for Indiana homeowners.',
  },
  {
    q: 'Is mortgage protection right for me if I already have life insurance?',
    a: "If your existing life insurance death benefit is large enough to pay off your mortgage AND replace your income for your family, you may already have adequate coverage. We'll review your current policy and help you decide if a standalone mortgage protection policy adds value or if increasing your existing coverage is more cost-effective.",
  },
];

export default function MortgageProtectionPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Mortgage Protection Insurance Indianapolis | Hoosier Life';

    const metaTags: { name?: string; property?: string; content: string }[] = [
      {
        name: 'description',
        content:
          "Protect your Indiana home from $25/mo. No exam required. Keep your family in their home if the unexpected happens. Call (317) 648-2983.",
      },
      { name: 'keywords', content: 'mortgage protection insurance Indianapolis, mortgage protection Indiana, home protection life insurance Indiana, mortgage life insurance Indianapolis' },
      { property: 'og:title', content: 'Mortgage Protection Insurance Indianapolis | Hoosier Life' },
      { property: 'og:description', content: "Protect your Indiana home from $25/mo. No exam required. Keep your family in their home if the unexpected happens." },
      { property: 'og:url', content: `${SITE_URL}/mortgage-protection` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/3a26475bb5133aba82271d0be40064cc.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Mortgage Protection Insurance Indianapolis | Hoosier Life' },
      { name: 'twitter:description', content: "Protect your Indiana home from $25/mo. No exam required. Keep your family in their home if the unexpected happens." },
      { name: 'twitter:image', content: 'https://readdy.ai/api/search-image?query=happy%20young%20family%20standing%20in%20front%20of%20beautiful%20suburban%20home%20in%20Indiana%20neighborhood%20parents%20with%20children%20showing%20joy%20security%20homeowners%20cream%20siding%20blue%20sky%20sunny%20day%20warm%20natural%20light&width=1200&height=630&seq=mp-og-image-v2&orientation=landscape' },
    ];

    const created: HTMLElement[] = [];

    metaTags.forEach((tag) => {
      const key = tag.name || tag.property || '';
      const attr = tag.name ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', tag.content);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/mortgage-protection`);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${SITE_URL}/mortgage-protection#service`,
        name: 'Mortgage Protection Insurance Indianapolis',
        description: 'Mortgage protection insurance for Indiana homeowners. Protect your family\'s home if the unexpected happens with affordable life insurance.',
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis' },
          { '@type': 'State', name: 'Indiana' },
        ],
        serviceType: 'Mortgage Protection Insurance',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '118',
          bestRating: '5',
          worstRating: '1',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Mortgage Protection', item: `${SITE_URL}/mortgage-protection` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.id = `schema-mortgage-protection-${i}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    // InsuranceProduct schema
    const ipSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceProduct',
      '@id': `${SITE_URL}/mortgage-protection#insurance-product`,
      name: 'Mortgage Protection Insurance',
      description: 'Life insurance designed to pay off a homeowner\'s mortgage if they pass away, protecting Indiana families from losing their home. Available with disability, critical illness, and return-of-premium riders.',
      url: `${SITE_URL}/mortgage-protection`,
      category: 'Life Insurance',
      brand: { '@type': 'Brand', name: 'Hoosier Life Insurance' },
      provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization` },
      isRelatedTo: { '@type': 'Organization', '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'State', name: 'Indiana' },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: 'Varies by mortgage amount, age, and health',
        },
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/mortgage-protection`,
      },
    };
    const ipScript = document.createElement('script');
    ipScript.id = 'schema-mortgage-protection-insurance-product';
    ipScript.type = 'application/ld+json';
    ipScript.textContent = JSON.stringify(ipSchema);
    document.head.appendChild(ipScript);
    scripts.push(ipScript);

    return () => {
      created.forEach((el) => { try { document.head.removeChild(el); } catch (e) { /* ignore */ } });
      scripts.forEach((s) => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } });
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <ProductNav />

      <main className="flex-grow">
        {/* Hero */}
        <section
          className="relative pt-28 pb-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://readdy.ai/api/search-image?query=happy%20young%20family%20standing%20in%20front%20of%20beautiful%20suburban%20home%20in%20Indiana%20neighborhood%20parents%20with%20children%20showing%20joy%20security%20homeowners%20financial%20protection&width=1440&height=700&seq=mortgage-protection-hero-1&orientation=landscape")',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/90 via-[#1A3A52]/70 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <span className="text-white">Mortgage Protection</span>
            </nav>
            <div className="max-w-2xl">
              <span className="inline-block bg-[#D97642] text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                Protect Your Home
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                Mortgage Protection Insurance for Indiana Homeowners
              </h1>
              <p className="text-xl text-white/85 mb-8 leading-relaxed">
                If something happens to you, your family still owes every mortgage payment. Mortgage protection insurance pays off your home so they never have to worry about losing it — starting under $30/month with no medical exam required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/instant-quote"
                  className="px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#c26535] transition-colors text-center whitespace-nowrap"
                >
                  Get Free Quote
                </Link>
                <Link
                  to="/book-appointment"
                  className="px-8 py-4 bg-white/10 text-white font-bold rounded-lg border border-white/30 hover:bg-white/20 transition-colors text-center whitespace-nowrap"
                >
                  Book Free Consultation
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-6">
                {['No Exam Options', 'From $25/month', 'Living Benefits Available', 'Fast Approval'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-white/90 text-sm">
                    <i className="ri-checkbox-circle-fill text-[#D97642]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Takeaways — AI SEO Signal */}
        <section className="py-8 bg-[#1A3A52]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
              </div>
              <div className="flex-1">
                <h2 className="text-white font-bold text-lg mb-3">Key Takeaways: Mortgage Protection Insurance in Indiana</h2>
                <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                  {[
                    'Mortgage protection pays off your remaining home loan balance if you die before it\'s paid off — your family keeps the home',
                    'Average Indiana home costs $245,000 — a 30-year term policy at age 35 protects it for as little as $30/month',
                    'Many plans include disability income riders — your mortgage is covered even if injury prevents you from working',
                    'No medical exam required on most plans — just health questions, and most Indiana homeowners qualify quickly',
                    'Unlike bank-sold mortgage insurance, your family receives the full benefit directly — not the lender',
                    'Level term policies protect beyond the mortgage — beneficiaries can use the benefit for income replacement too',
                  ].map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                      <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Mortgage Protection */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Why Indiana Homeowners Need Mortgage Protection</h2>
              <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
                The average Indiana home costs $245,000. If you died today, your family would still owe that balance — and have 30 days to figure it out. Mortgage protection makes sure the house is paid off so they can stay in the home they love, no matter what.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-home-heart-line',
                  title: 'Keep the Family Home',
                  desc: 'If you die with a mortgage, your family still owes every payment. Mortgage protection pays the balance so they can stay in the home they love.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Disability Coverage',
                  desc: 'Many policies include living benefits — if you become disabled and cannot work, your mortgage payments are covered while you recover.',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Affordable Coverage',
                  desc: 'Because coverage decreases as your mortgage balance decreases, premiums are often lower than standard term life insurance for the same initial amount.',
                },
                {
                  icon: 'ri-file-list-3-line',
                  title: 'Simplified Underwriting',
                  desc: 'Many mortgage protection plans require no medical exam — just a few health questions. Faster approval means your home is protected sooner.',
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Critical Illness Rider',
                  desc: 'Add a critical illness rider to cover your mortgage if you are diagnosed with cancer, heart attack, or stroke — before a death claim is ever needed.',
                },
                {
                  icon: 'ri-group-line',
                  title: "Both Spouses Covered",
                  desc: "We can cover both spouses under a joint policy, ensuring the home is protected no matter who is lost first. Simple, comprehensive, and affordable.",
                },
              ].map((item) => (
                <div key={item.title} className="bg-[#F9F7F4] rounded-xl p-6 border border-gray-100">
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-[#1A3A52]`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sample Rates */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl font-bold text-[#1A3A52] mb-6">Sample Mortgage Protection Rates</h2>
                <p className="text-[#4A5568] mb-6 leading-relaxed">
                  Based on a $250,000 mortgage in Indiana. Rates shown for non-smokers in good health. Actual rates depend on health, age, and carrier.
                </p>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#1A3A52] text-white">
                        <th className="px-4 py-3 text-left font-semibold">Age</th>
                        <th className="px-4 py-3 text-center font-semibold">20-Year Term</th>
                        <th className="px-4 py-3 text-center font-semibold">30-Year Term</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { age: '30', t20: '$22/mo', t30: '$30/mo' },
                        { age: '35', t20: '$26/mo', t30: '$36/mo' },
                        { age: '40', t20: '$34/mo', t30: '$48/mo' },
                        { age: '45', t20: '$48/mo', t30: '$70/mo' },
                        { age: '50', t20: '$72/mo', t30: '$108/mo' },
                      ].map((row, i) => (
                        <tr key={row.age} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                          <td className="px-4 py-3 font-medium text-[#1A3A52]">{row.age}</td>
                          <td className="px-4 py-3 text-center text-[#4A5568]">{row.t20}</td>
                          <td className="px-4 py-3 text-center text-[#4A5568]">{row.t30}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-[#4A5568] mt-3">* Male non-smoker, standard health class. Female rates are typically 10–15% lower.</p>
              </div>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-[#1A3A52] mb-6">The Real Risk of Skipping Coverage</h3>
                  <div className="space-y-5">
                    {[
                      { stat: '$1,200', label: 'Average monthly mortgage payment in Indiana', icon: 'ri-home-line' },
                      { stat: '28%', label: 'Of Indiana families live paycheck to paycheck', icon: 'ri-wallet-line' },
                      { stat: '3 months', label: 'Average time before mortgage default after income loss', icon: 'ri-time-line' },
                      { stat: '$30/mo', label: 'Cost to protect a $250K home for a healthy 35-year-old', icon: 'ri-shield-check-line' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-xl text-[#D97642]`} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#1A3A52]">{item.stat}</p>
                          <p className="text-sm text-[#4A5568]">{item.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Options */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Mortgage Protection Options We Offer</h2>
              <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
                We customize your coverage based on your loan balance, term, and budget.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Decreasing Term (Traditional)',
                  badge: 'Most Affordable',
                  features: [
                    'Coverage decreases as mortgage balance decreases',
                    'Lowest monthly premium',
                    'Death benefit pays off exact mortgage balance',
                    'Best for budget-conscious homeowners',
                  ],
                },
                {
                  title: 'Level Term (Recommended)',
                  badge: 'Best Value',
                  features: [
                    'Fixed death benefit for entire term',
                    'Family can use benefit beyond mortgage payoff',
                    'Covers mortgage + income replacement',
                    'More flexibility, slightly higher premiums',
                  ],
                },
                {
                  title: 'Return of Premium',
                  badge: 'Premium Refund',
                  features: [
                    'All premiums returned if you outlive the term',
                    'Best of both worlds: protection + savings',
                    'Higher upfront premiums, zero net cost if no claim',
                    "Good for homeowners who want a 'no-lose' option",
                  ],
                },
                {
                  title: 'Universal Life (Permanent)',
                  badge: 'Permanent Option',
                  features: [
                    'Permanent coverage that never expires',
                    'Cash value growth that you can access',
                    'Flexible premiums after initial period',
                    'Best for homeowners wanting lifelong protection',
                  ],
                },
              ].map((plan) => (
                <div key={plan.title} className="bg-[#F9F7F4] rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#1A3A52]">{plan.title}</h3>
                    <span className="bg-[#D97642] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{plan.badge}</span>
                  </div>
                  <ul className="space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-[#4A5568]">
                        <i className="ri-checkbox-circle-fill text-[#D97642] mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Indiana Homeowners Who Chose Peace of Mind</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Jason & Amber R.',
                  city: 'Fishers, IN',
                  text: "We just bought our house and realized we had nothing protecting it. Benjamin walked us through everything in 30 minutes. We now have a $350,000 policy for $38/month. If something happens to either of us, the house is paid off. Total peace of mind.",
                  stars: 5,
                },
                {
                  name: 'Thomas W.',
                  city: 'Carmel, IN',
                  text: "I'm self-employed and the disability rider was a game-changer. If I'm injured and can't work, the policy covers my mortgage. Benjamin was the only agent who even mentioned this option to me.",
                  stars: 5,
                },
                {
                  name: 'Sandra K.',
                  city: 'Noblesville, IN',
                  text: 'My husband passed and the mortgage protection policy paid off our home within 60 days. My kids and I still live here, which means everything. Worth every single premium.',
                  stars: 5,
                },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#D97642]" />
                    ))}
                  </div>
                  <p className="text-[#4A5568] text-sm leading-relaxed mb-4">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A3A52] text-sm">{t.name}</p>
                    <p className="text-[#4A5568] text-xs">{t.city}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-[#F9F7F4] rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-[#1A3A52] text-sm leading-snug">{faq.q}</span>
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-[#D97642] text-xl flex-shrink-0`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-[#4A5568] text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Related Articles & Resources ── */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Continue Learning</span>
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">Related Articles &amp; Resources</h2>
              <p className="text-gray-600 max-w-xl mx-auto">Deepen your understanding of mortgage protection and life insurance for Indiana homeowners.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[
                {
                  type: 'Blog',
                  typeColor: 'bg-[#D97642] text-white',
                  icon: 'ri-article-line',
                  title: 'Mortgage Protection Insurance Indianapolis',
                  desc: 'How mortgage protection works, what it covers, and why Indiana homeowners need it — with real rate examples.',
                  to: '/blog/mortgage-protection-indianapolis',
                },
                {
                  type: 'Blog',
                  typeColor: 'bg-[#D97642] text-white',
                  icon: 'ri-scales-line',
                  title: 'Term vs. Whole Life in Indianapolis',
                  desc: 'Which policy type best protects your mortgage? Side-by-side cost comparison with Indianapolis homeowner examples.',
                  to: '/blog/term-vs-whole-life-indianapolis',
                },
                {
                  type: 'Blog',
                  typeColor: 'bg-[#D97642] text-white',
                  icon: 'ri-calculator-line',
                  title: 'How Much Life Insurance Do Indiana Families Need?',
                  desc: 'DIME method walkthrough to calculate exactly how much coverage your family and mortgage require.',
                  to: '/blog/how-much-life-insurance-indiana',
                },
                {
                  type: 'Blog',
                  typeColor: 'bg-[#D97642] text-white',
                  icon: 'ri-calendar-check-line',
                  title: 'Term Life Insurance Indianapolis',
                  desc: 'Everything about term life — the most affordable way to cover your mortgage and protect your family\'s income.',
                  to: '/blog/term-life-insurance-indianapolis',
                },
                {
                  type: 'Resource Guide',
                  typeColor: 'bg-[#1A3A52] text-white',
                  icon: 'ri-book-open-line',
                  title: 'How a Fishers Family Kept Their Home',
                  desc: 'A real Indiana family\'s story: how mortgage protection insurance paid off a $400K home in 14 days after tragedy.',
                  to: '/resources/mortgage-protection-fishers-family-story',
                },
                {
                  type: 'Resource Guide',
                  typeColor: 'bg-[#1A3A52] text-white',
                  icon: 'ri-file-list-3-line',
                  title: 'Term vs. Whole Life for Indianapolis Homeowners',
                  desc: 'In-depth comparison guide with real rate tables, hybrid strategies, and a clear decision framework.',
                  to: '/resources/term-vs-whole-life-indianapolis-homeowners',
                },
              ].map((card, i) => (
                <Link
                  key={i}
                  to={card.to}
                  className="group bg-white rounded-xl border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all p-6 cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${card.typeColor}`}>{card.type}</span>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-lg mb-4 group-hover:bg-[#D97642] transition-colors">
                    <i className={`${card.icon} text-[#D97642] text-lg group-hover:text-white`}></i>
                  </div>
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors leading-snug">{card.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
                </Link>
              ))}
            </div>

            {/* CTA Banner */}
            <div className="bg-[#1A3A52] rounded-2xl px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-white font-bold text-lg mb-1">Protect your home starting at $30/month.</p>
                <p className="text-white/70 text-sm">Licensed Indiana agents · No obligation · Most approvals in 24–48 hours</p>
              </div>
              <Link
                to="/instant-quote"
                className="inline-flex items-center gap-2 px-7 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-shield-check-line text-lg"></i>
                Get My Free Quote
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#1A3A52]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Protect Your Home — Get a Free Quote Today</h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              In just a few minutes, we can tell you exactly what it costs to protect your family's home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/instant-quote" className="px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#c26535] transition-colors whitespace-nowrap">
                Get Free Quote
              </Link>
              <a href="tel:3176482983" className="px-8 py-4 bg-white/10 text-white font-bold rounded-lg border border-white/30 hover:bg-white/20 transition-colors whitespace-nowrap">
                <i className="ri-phone-line mr-2" />
                (317) 648-2983
              </a>
            </div>
          </div>
        </section>

        <StarRatingDisplay rating={4.9} reviewCount={118} productName="Mortgage Protection Insurance Indiana" />
        <ServingCommunitySection productName="Mortgage Protection Insurance" productSlug="mortgage-protection" />

        {/* Related Services */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Explore More Coverage Options</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { to: '/term-life', icon: 'ri-calendar-check-line', title: 'Term Life Insurance', desc: 'Affordable protection for a specific period — covers mortgage plus income replacement for your family.' },
                { to: '/whole-life', icon: 'ri-shield-check-line', title: 'Whole Life Insurance', desc: 'Lifetime coverage with cash value growth — protect your home and build wealth simultaneously.' },
                { to: '/indexed-universal-life', icon: 'ri-line-chart-line', title: 'Indexed Universal Life (IUL)', desc: 'Market-linked growth with downside protection — build tax-deferred wealth while keeping your home covered.' },
                { to: '/universal-life', icon: 'ri-shield-star-line', title: 'Universal Life Insurance', desc: 'Flexible permanent coverage with adjustable premiums — ideal for homeowners whose income varies.' },
                { to: '/final-expense', icon: 'ri-hand-heart-line', title: 'Final Expense', desc: 'Affordable coverage for funeral costs and end-of-life expenses — no medical exam required.' },
                { to: '/annuities', icon: 'ri-funds-line', title: 'Annuities', desc: 'Secure retirement income and tax-deferred growth — plan beyond the mortgage for long-term security.' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:bg-[#F9F7F4] transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] transition-colors">
                    <i className={`${item.icon} text-2xl text-[#1A3A52] group-hover:text-white`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                  <p className="text-[#4A5568] text-sm mb-4">{item.desc}</p>
                  <div className="flex items-center text-[#D97642] font-semibold text-sm">
                    Learn More <i className="ri-arrow-right-line ml-1" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileProductCTABar productName="Mortgage Protection" quoteHref="/instant-quote" />
    </div>
  );
}
